import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import NotificationBell from '@/components/common/NotificationBell'
import { useAuthStore } from '@/stores/authStore'
import { useHasMounted } from '@/hooks/useHasMounted'
import { useSmartBack } from '@/lib/hooks/useSmartBack'

interface HeaderProps {
  /** 페이지 제목 */
  title?: string
  /** 뒤로가기 버튼 표시 여부 */
  showBack?: boolean
  /** 뒤로가기 클릭 시 커스텀 동작 */
  onBack?: () => void
  /** 우측 액션 영역 */
  rightAction?: React.ReactNode
  /**
   * 우측 기본 알림 벨 표시 여부. 기본값 false.
   * 이 서브헤더를 쓰는 페이지는 대부분 상단 글로벌 헤더(MobileHeader)의 벨과
   * 중복되므로 기본적으로 끈다. 글로벌 헤더가 없는 독립 페이지에서만 true로 켠다.
   */
  showNotification?: boolean
  /** 추가 CSS 클래스 */
  className?: string
}

/**
 * 상단 헤더 컴포넌트.
 *
 * - 뒤로가기 버튼 (선택)
 * - 중앙 제목
 * - 우측 액션 영역
 * - 높이: 56px, 최소 터치 영역 48dp 준수
 */
export default function Header({
  title,
  showBack = false,
  onBack,
  rightAction,
  showNotification = false,
  className,
}: HeaderProps) {
  const smartBack = useSmartBack()
  const hasMounted = useHasMounted()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      smartBack()
    }
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-40 flex h-14 items-center border-b border-cb-gray-200 bg-white px-[var(--spacing-page-x)]',
        className
      )}
    >
      {/* 좌측: 뒤로가기 */}
      <div className="flex w-12 items-center justify-start">
        {showBack && (
          <button
            onClick={handleBack}
            className="flex h-12 w-12 items-center justify-center rounded-full hover:bg-cb-gray-100 transition-colors -ml-2"
            aria-label="뒤로 가기"
          >
            <ArrowLeft size={24} className="text-cb-gray-900" />
          </button>
        )}
      </div>

      {/* 중앙: 제목 (앱바 라벨 — 페이지 주제목이 아니므로 h1 아님. h1은 각 페이지 본문에 1개) */}
      <p className="flex-1 text-center text-h3 font-semibold text-cb-gray-900 truncate">
        {title}
      </p>

      {/* 우측: 액션 */}
      <div className="flex w-12 items-center justify-end">
        {rightAction ?? (showNotification && hasMounted && isAuthenticated ? <NotificationBell /> : null)}
      </div>
    </header>
  )
}
